import type { DataDrivenPropertyValueSpecification, ExpressionSpecification } from 'maplibre-gl'

/** Format depth number for legend labels (trim trailing zeros). */
export function formatDepth(m: number): string {
  return m.toFixed(2).replace(/\.?0+$/, '')
}

/** Labels for exclusive bands from inclusive break edges [min, ..., max]. */
export function floodDepthLabels(breaks: readonly number[]): string[] {
  if (breaks.length < 2) return []
  const labels: string[] = []
  for (let i = 0; i < breaks.length - 1; i++) {
    const low = breaks[i]!
    const high = breaks[i + 1]!
    const isFirst = i === 0
    const isTop = i === breaks.length - 2
    if (isFirst) {
      labels.push(`<${formatDepth(high)}m`)
    } else if (isTop) {
      labels.push(`${formatDepth(low)}m+`)
    } else {
      labels.push(`${formatDepth(low)}–${formatDepth(high)}m`)
    }
  }
  return labels
}

/**
 * Map feature `contour` (band upper bound, meters) → legend label.
 * Returns null when contour is not one of the break upper edges.
 */
export function floodDepthLabelForContour(
  contour: number,
  breaks: readonly number[]
): string | null {
  const labels = floodDepthLabels(breaks)
  for (let i = 1; i < breaks.length; i++) {
    if (breaks[i] === contour) return labels[i - 1] ?? null
  }
  return null
}

/**
 * MapLibre step paint on feature `contour` (band upper bound in meters).
 * `breaks` is [min, edge1, ..., max]; colors indexed per band.
 */
export function floodDepthFillColor(
  breaks: readonly number[],
  colormap: readonly string[]
): DataDrivenPropertyValueSpecification<string> {
  const nBands = breaks.length - 1
  const colors = Array.from({ length: nBands }, (_, i) => {
    if (nBands === 1) return colormap[0]!
    const t = i / (nBands - 1)
    const idx = Math.round(t * (colormap.length - 1))
    return colormap[idx]!
  })

  // step stops = upper bounds of bands after the first
  const stops: Array<number | string> = []
  for (let i = 1; i < nBands; i++) {
    stops.push(breaks[i + 1]!, colors[i]!)
  }

  return [
    'step',
    ['get', 'contour'],
    colors[0]!,
    ...stops,
  ] as ExpressionSpecification as DataDrivenPropertyValueSpecification<string>
}
